import { formatBytes } from '../lib/api.js';
import { isIOS } from '../lib/downloadAll.js';
import { IconDownload, IconClose } from '../lib/icons.jsx';

/**
 * Download options as a bottom sheet. Presents honest choices rather than one
 * "download all" that behaves differently per platform: photos and films
 * separately (the film is the multi-GB item most people don't want on a phone),
 * plus everything. Reasoning for individual-file downloads vs. zip lives in
 * gallery.js.
 */
export function DownloadSheet({ gallery, photos, videos, onClose, onChoose }) {
  const photoBytes = photos.reduce((n, p) => n + p.size_bytes, 0);
  const videoBytes = videos.reduce((n, v) => n + v.size_bytes, 0);
  const ios = isIOS();

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="spread" style={{ marginBottom: '1.25rem' }}>
          <div>
            <span className="label">Download</span>
            <h2 className="serif" style={{ marginTop: '0.35rem' }}>Save your gallery</h2>
          </div>
          <button className="icon-only ghost" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        <div className="stack" style={{ gap: '0.6rem' }}>
          {gallery.allow_photo_download && photos.length > 0 && (
            <button className="dl-option" onClick={() => onChoose('photo')}>
              <span>
                <b>All photographs</b>
                <span className="tiny">{photos.length} originals · {formatBytes(photoBytes)}</span>
              </span>
              <IconDownload />
            </button>
          )}
          {gallery.allow_video_download && videos.length > 0 && (
            <button className="dl-option" onClick={() => onChoose('video')}>
              <span>
                <b>{videos.length > 1 ? 'All films' : 'The film'}</b>
                <span className="tiny">{videos.length > 1 ? `${videos.length} films · ` : ''}{formatBytes(videoBytes)} · best on Wi-Fi</span>
              </span>
              <IconDownload />
            </button>
          )}
          {gallery.allow_photo_download && gallery.allow_video_download && photos.length > 0 && videos.length > 0 && (
            <button className="dl-option" onClick={() => onChoose('all')} style={{ borderColor: 'var(--ink)' }}>
              <span>
                <b>Everything</b>
                <span className="tiny">{formatBytes(photoBytes + videoBytes)} in total</span>
              </span>
              <IconDownload />
            </button>
          )}
        </div>

        <p className="tiny" style={{ marginTop: '1rem', marginBottom: 0 }}>
          {ios
            ? 'On iPhone these save one at a time into Files, and Safari will ask for each — for the full set, a computer is easier. To put a single photograph straight in your camera roll, open it and tap Download, then choose Save Image.'
            : 'Files download individually to keep every original at full quality. If your browser asks permission to download multiple files, choose Allow.'}
        </p>
      </div>
    </div>
  );
}

/**
 * Progress for a bulk download. The wording is honest: files were "sent to your
 * downloads", not that they finished — once a file is handed to the browser's
 * download manager we get no completion event (see downloadAll.js).
 */
export function BulkToast({ bulk, onClose }) {
  if (bulk.phase === 'error') {
    return (
      <div className="progress-toast">
        <div className="spread">
          <span style={{ color: '#9c2b26' }}>{bulk.error}</span>
          <button className="ghost sm" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }
  const pct = bulk.total ? Math.round((bulk.triggered / bulk.total) * 100) : 5;
  return (
    <div className="progress-toast">
      <div className="spread" style={{ marginBottom: '0.5rem' }}>
        <span className="label">
          {bulk.phase === 'preparing'
            ? 'Preparing…'
            : bulk.phase === 'done'
              ? 'Sent to your downloads'
              : `Sending ${bulk.triggered} of ${bulk.total}`}
        </span>
        <button className="ghost sm" onClick={onClose}>Hide</button>
      </div>
      <div className="bar">
        <div style={{ width: `${pct}%` }} />
      </div>
      <p className="tiny" style={{ margin: '0.5rem 0 0' }}>
        {bulk.totalBytes ? `${formatBytes(bulk.totalBytes)} total. ` : ''}
        Keep this tab open while they download. Anything missed can be saved individually from any photo.
      </p>
    </div>
  );
}

/** A film's display name: photographer title, else a tidied-up filename. */
export function filmName(video, index) {
  if (video.title) return video.title;
  const base = (video.filename || `Film ${index + 1}`).replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
  return base || `Film ${index + 1}`;
}

export function formatDuration(s) {
  if (!s) return '';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}
